import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EditKeyPermissionsDialog } from '@/components/EditKeyPermissionsDialog';
import { IssueKeyDialog } from '@/components/IssueKeyDialog';
import {
  fetchOrg,
  fetchOrgKeys,
  issueOrgKey,
  revokeOrgKey,
  setOrgKeyPermissions,
  setOrgPlan,
} from '@/api/orgs';
import type { ApiKeyView } from '@/api/types';
import { useCan } from '@/auth/session';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/ui/DataTable';
import { PageHeader } from '@/ui/PageHeader';
import { QueryError } from '@/ui/QueryError';
import { PageSpinner } from '@/ui/Spinner';
import { ORG_STATUS_TONE } from './OrgTable';
import { TransferDialog } from './TransferDialog';

export function OrgDetailPage() {
  const { orgId = '' } = useParams();
  const can = useCan();
  const queryClient = useQueryClient();
  const [transferOpen, setTransferOpen] = useState(false);

  const detail = useQuery({ queryKey: ['orgs', orgId], queryFn: () => fetchOrg(orgId) });

  const changePlan = useMutation({
    mutationFn: (planId: string) => setOrgPlan(orgId, planId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['orgs'] }),
  });

  if (detail.isPending) return <PageSpinner label="Loading organization" />;
  if (detail.isError)
    return <QueryError error={detail.error} onRetry={() => void detail.refetch()} />;

  const { org, partner, plan, direct, limits, transfers, assignable_plans, month_to_date } =
    detail.data;

  return (
    <>
      <PageHeader
        title={org.name}
        description={`${partner.name}${org.external_ref ? ` · ${org.external_ref}` : ''}`}
        actions={
          can('orgs.transfer') && (
            <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">Transfer</Button>
              </DialogTrigger>
              {transferOpen && (
                <TransferDialog orgId={orgId} onDone={() => setTransferOpen(false)} />
              )}
            </Dialog>
          )
        }
      />

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle>Plan</CardTitle>
            <CardDescription>{plan ? plan.name : 'No plan — no limits apply.'}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={ORG_STATUS_TONE[org.status]}>{org.status}</Badge>
            {direct && can('directOrgs.changePlan') && (
              <Select
                value={plan?.id ?? ''}
                onValueChange={(value) => changePlan.mutate(value)}
                disabled={changePlan.isPending}
              >
                <SelectTrigger aria-label="Change plan" className="w-48">
                  <SelectValue placeholder="Assign a plan" />
                </SelectTrigger>
                <SelectContent>
                  {assignable_plans.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Usage against limits</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {limits.map((limit) => (
            <div key={limit.key} className="rounded-lg border p-3">
              <div className="text-sm text-muted-foreground">{limit.label}</div>
              <div className={limit.at_limit ? 'font-medium text-destructive' : 'font-medium'}>
                {limit.used} / {limit.limit ?? 'unlimited'}
              </div>
              {limit.at_limit && <Badge variant="destructive">At limit</Badge>}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Month to date</CardTitle>
          <CardDescription>
            From the nightly rollup ({detail.data.usage_from} to {detail.data.usage_to}).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-4">
            {Object.entries(month_to_date).map(([event, count]) => (
              <div key={event} className="flex justify-between">
                <dt className="text-muted-foreground">{event}</dt>
                <dd>{count}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Transfer history</CardTitle>
        </CardHeader>
        <CardContent>
          {transfers.length === 0 ? (
            <p className="text-sm text-muted-foreground">Never transferred.</p>
          ) : (
            <ul className="flex flex-col gap-2 text-sm">
              {transfers.map((transfer) => (
                <li key={transfer.id} className="rounded-lg border p-3">
                  <div>
                    {transfer.from_partner.name} → {transfer.to_partner.name}
                    <span className="ml-2 text-muted-foreground">
                      {new Date(transfer.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-muted-foreground">{transfer.reason}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {direct && <OrgKeysCard orgId={orgId} />}
    </>
  );
}

function OrgKeysCard({ orgId }: { orgId: string }) {
  const can = useCan();
  const queryClient = useQueryClient();
  const [issueOpen, setIssueOpen] = useState(false);
  const [editing, setEditing] = useState<ApiKeyView | null>(null);
  const keys = useQuery({ queryKey: ['orgs', orgId, 'keys'], queryFn: () => fetchOrgKeys(orgId) });
  const canManage = can('orgKeys.manage');

  const revoke = useMutation({
    mutationFn: (keyId: string) => revokeOrgKey(orgId, keyId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['orgs', orgId, 'keys'] }),
  });

  const columns: ColumnDef<ApiKeyView, never>[] = [
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'environment', header: 'Environment' },
    { accessorKey: 'key_prefix', header: 'Key prefix' },
    {
      accessorKey: 'permissions',
      header: 'Permissions',
      cell: ({ row }) => row.original.permissions.join(', '),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.status === 'ACTIVE' ? 'secondary' : 'outline'}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        canManage && row.original.status === 'ACTIVE' ? (
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(row.original)}>
              Edit permissions
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => revoke.mutate(row.original.id)}
            >
              Revoke
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>API keys</CardTitle>
        {canManage && keys.data && (
          <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
            <DialogTrigger asChild>
              <Button>Issue key</Button>
            </DialogTrigger>
            <IssueKeyDialog
              key={String(issueOpen)}
              issueKey={(request) => issueOrgKey(orgId, request)}
              invalidateKey={['orgs', orgId, 'keys']}
              availablePermissions={keys.data.available_permissions}
              onDone={() => setIssueOpen(false)}
            />
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {keys.isError ? (
          <QueryError error={keys.error} onRetry={() => void keys.refetch()} />
        ) : (
          <DataTable
            columns={columns}
            data={keys.data?.keys ?? []}
            getRowId={(row) => row.id}
            isLoading={keys.isPending}
            empty="No API keys yet."
          />
        )}
      </CardContent>
      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        {editing && keys.data && (
          <EditKeyPermissionsDialog
            apiKey={editing}
            savePermissions={(keyId, permissions) =>
              setOrgKeyPermissions(orgId, keyId, permissions)
            }
            invalidateKey={['orgs', orgId, 'keys']}
            availablePermissions={keys.data.available_permissions}
            onDone={() => setEditing(null)}
          />
        )}
      </Dialog>
    </Card>
  );
}
